import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, GridWrapper, Row } from '@yme/atoms/grid';
import { InfoBlockEmptyResults } from '@yme/atoms/info-block-empty-results';
import { NOT_FOUND_URL, SEGMENT_PRICE_EXTRA_INFORMATION_TOOLTIP_TEXT } from '@yme/common/constants';
import { addComparisonProduct, removeComparisonProduct, } from '@yme/components/Comparison/redux/actions';
import { getComparisonProductsKeys } from '@yme/components/Comparison/redux/selectors';
import { FilterCompareCard } from '@yme/molecules/cards';
import { MAX_COMPARE_PRODUCTS } from '@yme/molecules/cards/compare-product-card';
import { GridSkeleton } from '@yme/molecules/grid-skeleton';
import { getIsDriverLicenseEnabled, getIsInAuthor, getLanguageRootPath, getPDPLinks, getRootLang, } from '@yme/selectors/common';
import { ELECTRIC_VEHICLE_ICON, PRODUCT_DRIVER_LICENCE_CATEGORY, PRODUCT_YEAR_ATTR, } from '@yme/selectors/constants';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { buildKey, check35KWPowerVersionExist, checkYAMTTransmissionExist, getAttributeByName, getIsNewLabelForUnitShowed, } from '@yme/utils/common';
import { STUDIO_IMAGE } from '@yme/utils/constants';
import { getPricesValues } from '@yme/utils/formatPrice';
import { getLatestModelTitle, getPdpLinkByCategory, getProductURL, getVariantWithAvailableLowestPrise, } from '@yme/utils/product';
import { ContainerStyled } from './styled';
export function FilterResults(props) {
    const { currentPerPageValue, productListData, labels, isRightToLeft = false, isProductsComparisonDisabled = false, isInEditor, isLoaded, } = props;
    const comparisonProductKeys = useSelector(getComparisonProductsKeys);
    const isDriverLicenseEnabled = useSelector(getIsDriverLicenseEnabled);
    const pdpLinks = useSelector(getPDPLinks);
    const isInAuthor = useSelector(getIsInAuthor);
    const languageRootPath = useSelector(getLanguageRootPath);
    const language = useSelector(getRootLang);
    const dispatch = useDispatch();
    const handleAddComparisonProduct = useCallback((product) => () => {
        dispatch(addComparisonProduct(product));
    }, [dispatch]);
    const handleRemoveComparisonProduct = useCallback((productKey) => () => {
        dispatch(removeComparisonProduct(productKey));
    }, [dispatch]);
    if (!productListData) {
        return null;
    }
    if (!isLoaded && !isInEditor) {
        return (React.createElement(Container, { expanded: true, width: "100%" },
            React.createElement(Row, null,
                React.createElement(GridSkeleton, { currentPerPageValue: currentPerPageValue }),
                ";")));
    }
    if (isLoaded && productListData?.length === 0) {
        return (React.createElement(GridWrapper, { containerProps: {
                width: '100%',
            } },
            React.createElement(FlexWrapper, { minHeight: "120px", isFullWidth: true, alignItems: "center", justifyContent: "center" },
                React.createElement(InfoBlockEmptyResults, { titleEmptyResults: labels.titleEmptyResults, descriptionEmptyResults: labels.descriptionEmptyResults, isFullWidth: true }))));
    }
    return (React.createElement(ContainerStyled, { expanded: true, width: "100%" },
        React.createElement(Row, null, productListData?.map((item, index) => {
            const id = item?.id || item?.slug || '';
            const key = buildKey(id, index);
            const isNewLabelShowed = getIsNewLabelForUnitShowed(item);
            const masterVariant = item.variants.find((i) => i.master);
            const { variantWithLowestPrice, isLowestPriseExist } = getVariantWithAvailableLowestPrise(item, masterVariant);
            const lowestPriceVariantImage = variantWithLowestPrice?.images.find((i) => i.url.toLowerCase().includes(STUDIO_IMAGE));
            const masterVariantImage = masterVariant?.images.find((i) => i.url.toLowerCase().includes(STUDIO_IMAGE));
            const image = lowestPriceVariantImage || masterVariantImage;
            const sku = variantWithLowestPrice?.sku || masterVariant?.sku || '';
            const prices = variantWithLowestPrice?.prices || [];
            const { priceValue, discountedValue } = getPricesValues({
                prices,
                language,
            });
            const attributes = variantWithLowestPrice?.attributes ?? [];
            const driverLicences = getAttributeByName(attributes, PRODUCT_DRIVER_LICENCE_CATEGORY);
            const productYear = getAttributeByName(attributes, PRODUCT_YEAR_ATTR);
            const electricVehicleIcon = getAttributeByName(attributes, ELECTRIC_VEHICLE_ICON);
            const extraInformationTooltipText = getAttributeByName(attributes, SEGMENT_PRICE_EXTRA_INFORMATION_TOOLTIP_TEXT);
            const title = getLatestModelTitle({
                isLatestModel: item.latestModel ?? false,
                name: item?.name ?? '',
                productYear,
            });
            const pdpPath = getPdpLinkByCategory(pdpLinks, item.categories);
            const itemUrl = getProductURL({
                path: pdpPath,
                isInAuthor,
                slug: item.slug,
                sku,
            });
            const isYAMTIconShowed = checkYAMTTransmissionExist(item);
            const is35KWIconShowed = check35KWPowerVersionExist(item);
            return (React.createElement(Col, { md: 4, lg: 4, key: key },
                React.createElement(FilterCompareCard, { isComparisonFilterResult: true, title: title || '', price: priceValue, isNewLabelShowed: isNewLabelShowed, discountedValue: discountedValue, extraInformationTooltipText: extraInformationTooltipText, isLabelFromVisible: isLowestPriseExist, driverLicense: isDriverLicenseEnabled ? driverLicences : undefined, electricVehicleIcon: electricVehicleIcon, image: image, isYAMTIconShowed: isYAMTIconShowed, is35KWIconShowed: is35KWIconShowed, idCard: id, url: itemUrl || `${languageRootPath}/${NOT_FOUND_URL}`, isRightToLeft: isRightToLeft, isChecked: comparisonProductKeys.some((productKey) => productKey === item.key), isCheckBoxNeeded: !isProductsComparisonDisabled, isDisabled: comparisonProductKeys.length === MAX_COMPARE_PRODUCTS &&
                        !comparisonProductKeys.includes(item.key), sku: sku ?? '', handleAddComparisonProduct: handleAddComparisonProduct(item), handleRemoveComparisonProduct: handleRemoveComparisonProduct(String(item.key)) })));
        }))));
}
